/**
 * UI DESIGN SYSTEM CONSTANTS
 * 
 * Single source of truth for z-index layers, modal heights, and motion timing.
 * Use these constants to ensure consistent layering and behavior across all UI components.
 * 
 * Z-INDEX HIERARCHY:
 * - z-40: Overlays (OverlayPage, Sidebar) - slide-over panels
 * - z-50: Standard Dialogs - form modals, confirmations
 * - z-60: Alert Dialogs - critical confirmations, destructive actions
 * - z-100: Toasts - highest priority notifications
 * 
 * MODAL HEIGHTS:
 * - max-h-[85vh]: Standard modals (default)
 * - max-h-[90vh]: Documented exception (OnboardingModal only - multi-step wizard)
 * 
 * MOTION SYSTEM: "Smooth" feel
 * - Semantic duration buckets optimized for OrderFlow's calm aesthetic
 * - Micro-interactions stay fast; overlays/drawers glide in smoothly
 * 
 * MOTION RULES:
 * 1. Never add delay unless synchronizing a close animation
 * 2. Keep tap → result interactions in instant/fast
 * 3. Reduced motion: everything collapses to instant
 * 4. Infinite loops: decorative only, marketing pages, behind prefers-reduced-motion
 */

/**
 * Z-index hierarchy for predictable stacking behavior.
 * Higher numbers appear above lower numbers.
 */
export const Z_INDEX = {
  /** Full-page slide-over panels (OverlayPage) */
  overlay: 40,
  /** Sidebar and navigation elements */
  sidebar: 40,
  /** Standard dialogs and modals */
  dialog: 50,
  /** Alert dialogs for critical confirmations */
  alert: 60,
  /** Toast notifications (highest priority) */
  toast: 100,
} as const;

/**
 * Standardized max-height values for consistent scroll behavior.
 * Use 'standard' for most modals; 'tall' only for documented exceptions.
 */
export const MODAL_HEIGHT = {
  /** Standard modal height (default) - 85vh */
  standard: "85vh",
  /** Tall modal height (onboarding wizards, complex forms) - 90vh */
  tall: "90vh",
} as const;

/**
 * MOTION SYSTEM - "Smooth" feel
 * 
 * Semantic duration buckets optimized for OrderFlow's calm aesthetic.
 * Micro-interactions stay fast; overlays/drawers glide in smoothly.
 * 
 * Usage:
 * - Framer Motion: duration: toSeconds(MOTION.duration.slow)
 * - Tailwind: Use corresponding duration-[Xms] classes directly
 */
export const MOTION = {
  /** Duration values in milliseconds */
  duration: {
    /** 120ms - Tiny feedback (hover, press, focus ring) */
    instant: 120,
    /** 180ms - Quick UI feedback (dropdowns, accordions, toggles) */
    fast: 180,
    /** 240ms - Standard transitions (dialogs, fades, page) */
    normal: 240,
    /** 320ms - Smooth glides (overlays, drawers, sheets) */
    slow: 320,
    /** 420ms - Elaborate entrances (empty states, staged) */
    elaborate: 420,
  },
  /** Easing curves for Framer Motion */
  easing: {
    /** Standard ease-out for most UI */
    standard: [0.4, 0, 0.2, 1] as const,
    /** Glide in - for entering overlays/drawers */
    decelerate: [0, 0, 0.2, 1] as const,
    /** Quick exit - for closing/dismissing */
    accelerate: [0.4, 0, 1, 1] as const,
  },
} as const;

/** Helper: convert ms to seconds for Framer Motion */
export const toSeconds = (ms: number) => ms / 1000;

/** 
 * Get duration with reduced motion fallback.
 * All durations collapse to instant when user prefers reduced motion.
 */
export const getMotionDuration = (duration: number, reducedMotion: boolean) =>
  reducedMotion ? MOTION.duration.instant : duration;

/**
 * Unified timing for modal transitions.
 * @deprecated Use MOTION.duration.normal instead
 */
export const MODAL_ANIMATION = {
  /** Standard fade/slide duration in milliseconds */
  duration: MOTION.duration.normal,
} as const;

/**
 * Tailwind class helpers for z-index.
 * Use these when you need the Tailwind class string.
 */
export const Z_INDEX_CLASSES = {
  overlay: "z-40",
  sidebar: "z-40",
  dialog: "z-50",
  alert: "z-60",
  toast: "z-[100]",
} as const;

/**
 * SPINNER SIZE CONVENTION
 * 
 * Standardized spinner sizes for consistent loading indicators:
 * - sm: For small buttons (h-7, h-8)
 * - default: For standard buttons (h-9, h-10)
 * - lg: For large buttons (h-11+)
 * - page: For page/section loading states
 */
export const SPINNER_SIZE = {
  /** Small buttons (h-7, h-8): Loader2 h-3 w-3 */
  sm: "h-3 w-3",
  /** Default buttons (h-9, h-10): Loader2 h-4 w-4 */
  default: "h-4 w-4",
  /** Large buttons (h-11+): Loader2 h-5 w-5 */
  lg: "h-5 w-5",
  /** Page/section loaders: Loader2 h-8 w-8 */
  page: "h-8 w-8",
} as const;

/**
 * SIDEBAR SYSTEM
 * 
 * Centralized sidebar width, animation, and persistence tokens.
 * Two states only: expanded / collapsed. Push-content layout via CSS Grid.
 * 
 * Width tokens are tunable for QA adjustments.
 * Animation follows the MOTION.duration.slow bucket for smooth glides.
 */
export const SIDEBAR = {
  /** Collapsed width - icons only (px) */
  collapsed: 64,
  /** Expanded width - full labels (px) - tuned tighter */
  expanded: 224,
  /** Animation duration - matches MOTION.duration.slow for glide feel */
  animationMs: MOTION.duration.slow,
  /** localStorage key for state persistence */
  storageKey: "orderflow.sidebar.state",
  /** Responsive breakpoint - matches useIsMobile (px) */
  breakpoint: 768,
} as const;

/**
 * STANDARD SIDE SHEET SYSTEM
 * 
 * Canonical baseline for ALL side sheets in OrderFlow.
 * No variants. No exceptions. No task-based adjustments.
 * 
 * Any future customization MUST extend this baseline
 * and be documented separately.
 * 
 * Version: 1.0 (Baseline Freeze)
 * 
 * Desktop: w-full sm:max-w-[400px] sm:right-4 sm:left-auto
 * Mobile: w-full (full screen)
 */
export const SIDE_SHEET = {
  /** Max-width of sheet (400dp) */
  maxWidth: 400,
  /** Detached margin from RIGHT edge only (16dp) - top/bottom: 0 */
  detachedMarginRight: 16,
  /** Horizontal content padding (24dp) = px-6 */
  paddingX: 24,
  /** Header/content vertical padding (16dp) = py-4 */
  paddingY: 16,
  /** Footer bottom padding (24dp) = pb-6 */
  footerPaddingBottom: 24,
  /** Footer total height (72dp) */
  footerHeight: 72,
  /** Section-to-section spacing (24dp) = space-y-6 */
  sectionSpacing: 24,
  /** Element-to-element spacing (12dp) = space-y-3 */
  elementSpacing: 12,
  /** Backdrop opacity: 40% - visible separation without modal oppression */
  backdropOpacity: 40,
  /** Shadow level: lg - professional ops feel, not floating modal */
  shadow: "lg" as const,
  /** Border radius on desktop: rounded-l-lg */
  radius: "rounded-l-lg" as const,
} as const;

/**
 * Tailwind class helpers for Standard Side Sheet.
 * Use these in components for consistent sizing.
 */
export const SIDE_SHEET_CLASSES = {
  /** Desktop panel: responsive max-width with right margin */
  panel: "w-full sm:w-full sm:max-w-[400px] sm:right-4 sm:left-auto",
  /** Horizontal padding: px-6 (24dp) */
  paddingX: "px-6",
  /** Vertical padding: py-4 (16dp) */
  paddingY: "py-4",
  /** Footer padding: pt-4 pb-6 (16dp top, 24dp bottom) */
  footerPadding: "pt-4 pb-6",
  /** Section spacing: space-y-6 (24dp) */
  sectionSpacing: "space-y-6",
  /** Element spacing: space-y-3 (12dp) */
  elementSpacing: "space-y-3",
  /** Input height responsive: h-11 mobile, h-10 desktop */
  inputHeight: "h-11 sm:h-10",
  /** Backdrop: bg-black/40 - visible separation without modal oppression */
  backdrop: "bg-black/40",
  /** Shadow: shadow-lg - professional ops feel */
  shadow: "shadow-lg",
  /** Desktop radius: sm:rounded-l-lg */
  radius: "sm:rounded-l-lg",
} as const;

/**
 * @deprecated Use SIDE_SHEET instead - kept for backward compatibility
 */
export const SHEET_WIDTH = {
  narrow: 400,
  wide: 400,
  contentMax: 400,
} as const;

/**
 * @deprecated Use SIDE_SHEET instead - kept for backward compatibility
 */
export const OVERLAY_TOKENS = {
  headerHeight: 56,
  footerHeight: SIDE_SHEET.footerHeight,
  padding: {
    mobile: 16,
    desktop: SIDE_SHEET.paddingX,
  },
  sectionSpacing: SIDE_SHEET.sectionSpacing,
  inputHeight: {
    desktop: 40,
    mobile: 44,
  },
} as const;

/**
 * @deprecated Use SIDE_SHEET_CLASSES.inputHeight instead
 */
export const OVERLAY_INPUT_CLASSES = {
  desktop: "h-10",
  mobile: "h-11",
  responsive: "h-11 sm:h-10",
} as const;
